import {
    Box,
    BreadcrumbGroup,
    BreadcrumbGroupProps,
    ButtonDropdown,
    Cards,
    Link,
    SegmentedControl,
    SpaceBetween,
    TextContent,
    Toggle,
} from '@cloudscape-design/components';
import '../styles/list-pages.scss';
import { useLocation } from 'react-router';
import { useTranslate } from 'src/i18n/useTranslate';
import { usePreferences } from 'src/components/providers/PreferencesProvider';
import { useFavorites } from 'src/components/providers/FavoritesProvider';
import { SearchResultsForCards } from 'src/types/searchResult';
import { ENGLISH_PATH, USA_PATH } from 'src/utils/searchUtils';
import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';
import { useAuth } from 'src/components/providers/AuthProvider';

interface ListPageProps {
    pageTitle?: string;
    items?: SearchResultsForCards[];
    searchResultsLoading?: boolean;
    longLoad?: boolean;
    appendedContent?: React.JSX.Element;
}

const ListPage = ({
    pageTitle,
    items = [],
    searchResultsLoading,
    longLoad = false,
    appendedContent,
}: ListPageProps) => {
    const t = useTranslate();
    const location = useLocation();
    const { listViewStyle, updateListViewStyleState } = usePreferences();
    const { addFavorite, deleteFavorite, isFavorite } = useFavorites();
    const { user } = useAuth();

    const secondBreadcrumb =
        location.pathname === '/search'
            ? t('search-breadcrumb', 'Search Results')
            : location.pathname === '/recent'
            ? t('recent-breadcrumb', 'Recently Viewed')
            : t('favorite-breadcrumb', 'My Favorites');

    const breadcrumbs = [
        { text: t('home-breadcrumb', 'Home'), href: '/' },
        { text: secondBreadcrumb },
    ] as BreadcrumbGroupProps.Item[];

    const isGridView = listViewStyle === 'grid-view';

    const clickSearchResultMetricsPublisher =
        initialMetricsPublisher.newChildActionPublisherForMethod(
            'ClickSearchResult'
        );
    const clickSearchResult = (lcmsXmlGUID: string) => {
        clickSearchResultMetricsPublisher.publish(
            new KatalMetrics.Metric.String(
                'gru.search.result.click.userId',
                user?.hashedUserId || ''
            )
        );
        clickSearchResultMetricsPublisher.publish(
            new KatalMetrics.Metric.String(
                'gru.search.result.click.topic',
                lcmsXmlGUID
            )
        );
    };

    return (
        <div data-list-page data-list-page-card-style={listViewStyle}>
            <SpaceBetween size="s">
                <div className="breadcrumbs-container">
                    <BreadcrumbGroup
                        items={breadcrumbs}
                        ariaLabel={t('breadcrumbs', 'Breadcrumbs')}
                    />
                </div>
                <div className="results-content">
                    <div className="cards-container">
                        <div className="title-container">
                            <SpaceBetween
                                size="xxl"
                                direction="horizontal"
                                data-title-and-buttons
                                className="flex-row justify-space-between"
                            >
                                <TextContent>
                                    <h1 style={{ marginBottom: '16px' }}>
                                        {pageTitle
                                            ? pageTitle
                                            : 'Showing 2202 German results for "Safety" available in Germany for RME'}
                                    </h1>
                                </TextContent>
                                <SegmentedControl
                                    data-segment-control
                                    selectedId={listViewStyle}
                                    onChange={({ detail }) =>
                                        updateListViewStyleState(
                                            detail.selectedId
                                        )
                                    }
                                    label="Segmented control with only icons"
                                    options={[
                                        {
                                            // iconName: "view-full",
                                            iconSvg: (
                                                <svg
                                                    width="12"
                                                    height="12"
                                                    viewBox="0 0 12 12"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M4 10.6667V9.33335H12V10.6667H4ZM4 6.66669V5.33335H12V6.66669H4ZM4 2.66669V1.33335H12V2.66669H4ZM1.33333 11.3334C0.966667 11.3334 0.652778 11.2028 0.391667 10.9417C0.130556 10.6806 0 10.3667 0 10C0 9.63335 0.130556 9.31947 0.391667 9.05835C0.652778 8.79724 0.966667 8.66669 1.33333 8.66669C1.7 8.66669 2.01389 8.79724 2.275 9.05835C2.53611 9.31947 2.66667 9.63335 2.66667 10C2.66667 10.3667 2.53611 10.6806 2.275 10.9417C2.01389 11.2028 1.7 11.3334 1.33333 11.3334ZM1.33333 7.33335C0.966667 7.33335 0.652778 7.2028 0.391667 6.94169C0.130556 6.68058 0 6.36669 0 6.00002C0 5.63335 0.130556 5.31947 0.391667 5.05835C0.652778 4.79724 0.966667 4.66669 1.33333 4.66669C1.7 4.66669 2.01389 4.79724 2.275 5.05835C2.53611 5.31947 2.66667 5.63335 2.66667 6.00002C2.66667 6.36669 2.53611 6.68058 2.275 6.94169C2.01389 7.2028 1.7 7.33335 1.33333 7.33335ZM1.33333 3.33335C0.966667 3.33335 0.652778 3.2028 0.391667 2.94169C0.130556 2.68058 0 2.36669 0 2.00002C0 1.63335 0.130556 1.31946 0.391667 1.05835C0.652778 0.797243 0.966667 0.666687 1.33333 0.666687C1.7 0.666687 2.01389 0.797243 2.275 1.05835C2.53611 1.31946 2.66667 1.63335 2.66667 2.00002C2.66667 2.36669 2.53611 2.68058 2.275 2.94169C2.01389 3.2028 1.7 3.33335 1.33333 3.33335Z"
                                                        fill="#1C1B1F"
                                                    />
                                                </svg>
                                            ),
                                            iconAlt: 'View as list',
                                            id: 'list-view',
                                        },
                                        {
                                            iconSvg: (
                                                <svg
                                                    width="13"
                                                    height="12"
                                                    viewBox="0 0 13 12"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        stroke-width="1"
                                                        d="M2.25065 11.6674C1.86107 11.6674 1.52756 11.5287 1.25013 11.2513C0.9727 10.9739 0.833984 10.6404 0.833984 10.2508C0.833984 9.86119 0.9727 9.52768 1.25013 9.25025C1.52756 8.97282 1.86107 8.83411 2.25065 8.83411C2.64023 8.83411 2.97374 8.97282 3.25117 9.25025C3.5286 9.52768 3.66732 9.86119 3.66732 10.2508C3.66732 10.6404 3.5286 10.9739 3.25117 11.2513C2.97374 11.5287 2.64023 11.6674 2.25065 11.6674ZM6.50065 11.6674C6.11107 11.6674 5.77756 11.5287 5.50013 11.2513C5.2227 10.9739 5.08398 10.6404 5.08398 10.2508C5.08398 9.86119 5.2227 9.52768 5.50013 9.25025C5.77756 8.97282 6.11107 8.83411 6.50065 8.83411C6.89023 8.83411 7.22374 8.97282 7.50117 9.25025C7.7786 9.52768 7.91732 9.86119 7.91732 10.2508C7.91732 10.6404 7.7786 10.9739 7.50117 11.2513C7.22374 11.5287 6.89023 11.6674 6.50065 11.6674ZM10.7507 11.6674C10.3611 11.6674 10.0276 11.5287 9.75013 11.2513C9.4727 10.9739 9.33398 10.6404 9.33398 10.2508C9.33398 9.86119 9.4727 9.52768 9.75013 9.25025C10.0276 8.97282 10.3611 8.83411 10.7507 8.83411C11.1402 8.83411 11.4737 8.97282 11.7512 9.25025C12.0286 9.52768 12.1673 9.86119 12.1673 10.2508C12.1673 10.6404 12.0286 10.9739 11.7512 11.2513C11.4737 11.5287 11.1402 11.6674 10.7507 11.6674ZM2.25065 7.41744C1.86107 7.41744 1.52756 7.27872 1.25013 7.00129C0.9727 6.72386 0.833984 6.39036 0.833984 6.00077C0.833984 5.61119 0.9727 5.27768 1.25013 5.00025C1.52756 4.72282 1.86107 4.58411 2.25065 4.58411C2.64023 4.58411 2.97374 4.72282 3.25117 5.00025C3.5286 5.27768 3.66732 5.61119 3.66732 6.00077C3.66732 6.39036 3.5286 6.72386 3.25117 7.00129C2.97374 7.27872 2.64023 7.41744 2.25065 7.41744ZM6.50065 7.41744C6.11107 7.41744 5.77756 7.27872 5.50013 7.00129C5.2227 6.72386 5.08398 6.39036 5.08398 6.00077C5.08398 5.61119 5.2227 5.27768 5.50013 5.00025C5.77756 4.72282 6.11107 4.58411 6.50065 4.58411C6.89023 4.58411 7.22374 4.72282 7.50117 5.00025C7.7786 5.27768 7.91732 5.61119 7.91732 6.00077C7.91732 6.39036 7.7786 6.72386 7.50117 7.00129C7.22374 7.27872 6.89023 7.41744 6.50065 7.41744ZM10.7507 7.41744C10.3611 7.41744 10.0276 7.27872 9.75013 7.00129C9.4727 6.72386 9.33398 6.39036 9.33398 6.00077C9.33398 5.61119 9.4727 5.27768 9.75013 5.00025C10.0276 4.72282 10.3611 4.58411 10.7507 4.58411C11.1402 4.58411 11.4737 4.72282 11.7512 5.00025C12.0286 5.27768 12.1673 5.61119 12.1673 6.00077C12.1673 6.39036 12.0286 6.72386 11.7512 7.00129C11.4737 7.27872 11.1402 7.41744 10.7507 7.41744ZM2.25065 3.16744C1.86107 3.16744 1.52756 3.02872 1.25013 2.75129C0.9727 2.47386 0.833984 2.14036 0.833984 1.75077C0.833984 1.36119 0.9727 1.02768 1.25013 0.750252C1.52756 0.472822 1.86107 0.334106 2.25065 0.334106C2.64023 0.334106 2.97374 0.472822 3.25117 0.750252C3.5286 1.02768 3.66732 1.36119 3.66732 1.75077C3.66732 2.14036 3.5286 2.47386 3.25117 2.75129C2.97374 3.02872 2.64023 3.16744 2.25065 3.16744ZM6.50065 3.16744C6.11107 3.16744 5.77756 3.02872 5.50013 2.75129C5.2227 2.47386 5.08398 2.14036 5.08398 1.75077C5.08398 1.36119 5.2227 1.02768 5.50013 0.750252C5.77756 0.472822 6.11107 0.334106 6.50065 0.334106C6.89023 0.334106 7.22374 0.472822 7.50117 0.750252C7.7786 1.02768 7.91732 1.36119 7.91732 1.75077C7.91732 2.14036 7.7786 2.47386 7.50117 2.75129C7.22374 3.02872 6.89023 3.16744 6.50065 3.16744ZM10.7507 3.16744C10.3611 3.16744 10.0276 3.02872 9.75013 2.75129C9.4727 2.47386 9.33398 2.14036 9.33398 1.75077C9.33398 1.36119 9.4727 1.02768 9.75013 0.750252C10.0276 0.472822 10.3611 0.334106 10.7507 0.334106C11.1402 0.334106 11.4737 0.472822 11.7512 0.750252C12.0286 1.02768 12.1673 1.36119 12.1673 1.75077C12.1673 2.14036 12.0286 2.47386 11.7512 2.75129C11.4737 3.02872 11.1402 3.16744 10.7507 3.16744Z"
                                                        fill="white"
                                                    />
                                                </svg>
                                            ),
                                            iconAlt: 'View as grid',
                                            id: 'grid-view',
                                        },
                                    ]}
                                />
                            </SpaceBetween>
                        </div>
                        <Cards
                            data-all-cards-container
                            ariaLabels={{
                                itemSelectionLabel: (e, t) =>
                                    `select ${t.name}`,
                                selectionGroupLabel: 'Item selection',
                            }}
                            loading={!items.length && searchResultsLoading}
                            cardDefinition={{
                                header: (item) => {
                                    return (
                                        <div
                                            data-list-page-card
                                            data-testid="list-page-card"
                                        >
                                            <Link
                                                href={`/${item.id}#/${item.lcmsXmlGuid}`}
                                                onFollow={() =>
                                                    clickSearchResult(
                                                        item.lcmsXmlGuid
                                                    )
                                                }
                                                fontSize="heading-m"
                                            >
                                                {item.name}
                                            </Link>
                                            <ButtonDropdown
                                                onItemClick={({ detail }) => {
                                                    const id = detail.id;

                                                    switch (id) {
                                                        case 'favorite':
                                                            deleteFavorite(
                                                                item.id
                                                            );
                                                            break;
                                                        case 'not-favorite':
                                                            addFavorite(
                                                                item.id
                                                            );
                                                            break;
                                                        case 'copy-link':
                                                            navigator.clipboard.writeText(
                                                                `${window.location.origin}/${item.id}`
                                                            );
                                                            break;
                                                        default:
                                                            break;
                                                    }
                                                }}
                                                data-list-page-card-dropdown-container
                                                data-testid={
                                                    'favorite-dropdown'
                                                }
                                                items={[
                                                    {
                                                        id: isFavorite(item.id)
                                                            ? 'favorite'
                                                            : 'not-favorite',
                                                        text: isFavorite(
                                                            item.id
                                                        )
                                                            ? t(
                                                                  'remove-from-favorites',
                                                                  'Remove from Favorites'
                                                              )
                                                            : t(
                                                                  'add-to-favorites',
                                                                  'Add to Favorites'
                                                              ),
                                                        iconName: 'heart',
                                                    },
                                                    {
                                                        id: 'copy-link',
                                                        text: t(
                                                            'landing_page_card_options_copy',
                                                            'Copy link'
                                                        ),
                                                        iconName: 'copy',
                                                    },
                                                ]}
                                                ariaLabel={t(
                                                    'control_instance',
                                                    'Control instance'
                                                )}
                                                variant="inline-icon"
                                            />
                                        </div>
                                    );
                                },
                                sections: [
                                    {
                                        id: 'description',
                                        header: '',
                                        content: (item) => (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: item.description,
                                                }}
                                            />
                                        ),
                                    },
                                    {
                                        id: 'path',
                                        header: '',
                                        content: (item) => (
                                            <TextContent>
                                                <p data-list-path>
                                                    <small>{item.path}</small>
                                                </p>
                                            </TextContent>
                                        ),
                                    },
                                    {
                                        id: 'locale',
                                        header: '',
                                        // if list view, show modified date and locale on same line. Otherwise modified date shows on next line
                                        content: (item) => (
                                            <TextContent>
                                                <SpaceBetween
                                                    direction="horizontal"
                                                    size="xxl"
                                                    data-list-page-list-view-bottom-info
                                                >
                                                    <p data-list-locale>
                                                        <small>
                                                            {item.locale}
                                                        </small>
                                                    </p>
                                                    <p data-list-last-modified>
                                                        <small>
                                                            {t(
                                                                'details-page-last-modified',
                                                                'Last modified on'
                                                            )}{' '}
                                                            <small
                                                                data-last-modified-date
                                                            >
                                                                {
                                                                    item.dateUpdated
                                                                }
                                                            </small>
                                                        </small>
                                                    </p>
                                                </SpaceBetween>
                                            </TextContent>
                                        ),
                                    },
                                ],
                            }}
                            cardsPerRow={[
                                { cards: 1 },
                                { minWidth: 500, cards: isGridView ? 3 : 1 },
                            ]}
                            items={items}
                            loadingText={
                                longLoad
                                    ? t(
                                          'gru_heavy_load',
                                          "Looks like GRU Search is under heavy load right now. We're still loading in the background.."
                                      )
                                    : t(
                                          'loading_resources',
                                          'Loading resources'
                                      )
                            }
                            trackBy="lcmsXmlGuid"
                            visibleSections={[
                                'description',
                                'path',
                                'locale',
                                'dateUpdated',
                            ]}
                        />
                        {appendedContent || null}
                    </div>
                </div>
            </SpaceBetween>
        </div>
    );
};

export default ListPage;
